import React, { useMemo } from "react";
import { Modal, Button, ResponsiveDataTable } from "../../../ui/index.ts";
import type { ResponsiveColumn } from "../../../ui/responsive-table/types.ts";
import { TailSpin } from "react-loader-spinner";

import {
  useEmployeeAllocations,
  type EmployeeAllocationDetailRow,
} from "../../../../hooks/time-allotment/use-employee-allocations.ts";

import styles from "./view-allocations-modal.module.css";

type ViewAllocationsTableRow = EmployeeAllocationDetailRow & {
  id: string;
} & Record<string, unknown>;

export type ViewAllocationsModalProps = {
  open: boolean;
  /** Numeric employee id from the PM members API (path param). */
  employeeId: string | null;
  employeeName: string;
  projectName: string;
  onClose: () => void;
};

export function ViewAllocationsModal({
  open,
  employeeId,
  employeeName,
  projectName,
  onClose,
}: ViewAllocationsModalProps) {
  const enabled = open && employeeId != null && employeeId !== "";
  const { rows, loading, error, refetch } = useEmployeeAllocations(
    employeeId,
    enabled,
  );

  const tableData: ViewAllocationsTableRow[] = useMemo(
    () =>
      rows.map((row, index) => ({
        ...row,
        id: `${row.projectName}-${index}`,
      })),
    [rows],
  );

  const columns = useMemo(
    (): ResponsiveColumn<ViewAllocationsTableRow>[] => [
      {
        id: "projectName",
        header: "Project",
        accessor: "projectName",
        sortable: true,
        truncate: true,
      },
      {
        id: "designation",
        header: "Designation",
        accessor: "designation",
        sortable: true,
        render: (row) => row.designation?.trim() || "—",
        getTitleText: (row) => row.designation?.trim() || "",
      },
      {
        id: "allocatedHours",
        header: "Allocated hours",
        accessor: "allocatedHours",
        sortable: true,
      },
      {
        id: "deliveryLeadName",
        header: "Delivery lead",
        accessor: "deliveryLeadName",
        sortable: true,
        render: (row) => row.deliveryLeadName || "—",
        getTitleText: (row) => row.deliveryLeadName || "",
      },
      {
        id: "projectManagerName",
        header: "Project manager",
        accessor: "projectManagerName",
        sortable: true,
        render: (row) => row.projectManagerName || "—",
        getTitleText: (row) => row.projectManagerName || "",
      },
    ],
    [],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Allocations"
      size="xl"
      footer={
        <Button type="button" variant="muted-outline" onClick={onClose}>
          Close
        </Button>
      }
    >
      <p className={`body-sm ${styles.subtitle}`}>
        {employeeName} · {projectName}
      </p>

      <div className={styles.tableWrap}>
        {loading && (
          <div
            className={styles.loadingWrap}
            role="status"
            aria-live="polite"
          >
            <TailSpin
              height="48"
              width="48"
              color="var(--primary-color)"
              visible={true}
            />
            <p className={styles.loadingText}>Loading allocations…</p>
          </div>
        )}

        {!loading && error && (
          <div className={styles.errorBlock}>
            <p className={`body-sm ${styles.errorText}`}>{error}</p>
            <Button
              type="button"
              variant="accent"
              onClick={() => void refetch()}
            >
              Try again
            </Button>
          </div>
        )}

        {!loading && !error && rows.length === 0 && (
          <p className={`body-sm ${styles.emptyText}`}>
            No allocation records found.
          </p>
        )}

        {!loading && !error && rows.length > 0 && (
          <ResponsiveDataTable<ViewAllocationsTableRow>
            columns={columns}
            data={tableData}
            pagination={false}
            aria-label="Employee allocation records"
          />
        )}
      </div>
    </Modal>
  );
}
